import { noop } from '@accedo/general-utils';

/**
 * Creates an event listener for mouse cursor visibility changes.
 * It requires MOUSE_ENABLED to be set to true in the webpack config to work.
 * It listens to platform-specific events to determine when the mouse cursor is visible or hidden.
 * Currently, it only supports WebOS (with magic remote) and Workstation if built with mouse support enabled (see webpack.config.ts).
 * @example```
 * useEffect(() => {
 *   const unsubscribe = createCursorVisibilityEvent((event) => {
 *     console.log('Mouse visibility changed:', event.detail.visibility);
 *   });
 *
 *   return () => {
 *     unsubscribe();
 *   };
 * }, []);
 * ```
 * @param cb - Callback function to call for mouse visibility changes.
 * @returns A function to remove the event listener.
 */
const createCursorVisibilityEvent = (cb: (event: CustomEvent<{ visibility: boolean }>) => void): (() => void) => {
  const callback = cb as (event: Event) => void;

  const mouseEnabled = Boolean(process.env.MOUSE_ENABLED);
  if (!mouseEnabled) {
    return noop; // noop unsubscribe
  }

  if (process.env.APP_PLATFORM === 'webos') {
    // webOS has native cursorStateChange event
    document.addEventListener('cursorStateChange', callback);

    return () => {
      document.removeEventListener('cursorStateChange', callback);
    };
  }

  if (process.env.APP_PLATFORM === 'workstation') {
    // Workstation: simulate cursorStateChange using mouseenter/mouseleave
    const mouseEnterHandler = (): void => {
      cb(
        new CustomEvent('cursorStateChange', {
          detail: { visibility: true },
        }),
      );
    };

    const mouseLeaveHandler = (): void => {
      cb(
        new CustomEvent('cursorStateChange', {
          detail: { visibility: false },
        }),
      );
    };

    document.addEventListener('mouseenter', mouseEnterHandler);
    document.addEventListener('mouseleave', mouseLeaveHandler);

    return () => {
      document.removeEventListener('mouseenter', mouseEnterHandler);
      document.removeEventListener('mouseleave', mouseLeaveHandler);
    };
  }

  // Unsupported platform
  return noop;
};

/**
 * Creates an event listener for mouse wheel events.
 * It requires MOUSE_ENABLED to be set to true in the webpack config to work.
 * @param cb - Callback function for the event listenerP.
 * @returns A function to remove the event listener.
 */
const createWheelEventListener = (cb: (event: WheelEvent) => void): (() => void) => {
  const mouseEnabled = Boolean(process.env.MOUSE_ENABLED);
  if (!mouseEnabled) {
    return noop; // noop unsubscribe
  }

  document.addEventListener('wheel', cb, { passive: true });

  return () => {
    document.removeEventListener('wheel', cb);
  };
};

export { createCursorVisibilityEvent, createWheelEventListener };
